import React from 'react';
import { Rect, Line, Ellipse } from 'react-konva';
import type { ThemePalette } from '../../types';

export interface NodeBodyProps {
  type: string;
  w: number;
  h: number;
  accentColor: string;
  isSelected: boolean;
  palette: ThemePalette;
  diamondRotation?: number;
  shapeRotation?: number;
}

/** Renders the background shape (shadow + body) for shaped node types
 *  (`diamond`, `database`, `queue`). Returns `null` for any other type so the caller
 *  can fall through to the default rectangular renderer. */
export const NodeBody: React.FC<NodeBodyProps> = ({ type, w, h, accentColor, isSelected, palette, shapeRotation = 0 }) => {
  const stroke = isSelected ? accentColor : palette.nodeStroke;
  const sw = isSelected ? 1.5 : 1;
  const fill = palette.nodeBody;
  const tint = accentColor + '18';

  const gSw = isSelected ? 2 : 1.5; // slightly thicker than default for visibility

  if (type === 'diamond') {
    const mx = w / 2, my = h / 2;
    const pts = [mx, 0, w, my, mx, h, 0, my];
    const shadowPts = [mx + 2, 4, w + 2, my + 4, mx + 2, h + 4, 2, my + 4];
    return (
      <>
        <Line points={shadowPts} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'database' || type === 'cylinder') {
    // Classic DB cylinder — top accent ellipse, body rect, bottom ellipse
    const eH = 16; // ellipse cap height
    const isSketchCylinder = type === 'cylinder';
    const bodyStroke = isSketchCylinder ? accentColor : stroke;
    const bodySw = isSketchCylinder ? (isSelected ? 2 : 1.5) : sw;
    return (
      <>
        {/* Shadow */}
        <Rect
          x={2} y={4 + eH / 2} width={w} height={h - eH}
          fill={palette.nodeShadow}
          listening={false} perfectDrawEnabled={false}
        />
        {/* Body rect (sides only — top/bottom covered by ellipses) */}
        <Rect
          x={0} y={eH / 2} width={w} height={h - eH}
          fill={fill} stroke={bodyStroke} strokeWidth={bodySw}
          perfectDrawEnabled={false}
        />
        {/* Tint overlay on body */}
        <Rect
          x={0} y={eH / 2} width={w} height={h - eH}
          fill={tint}
          listening={false} perfectDrawEnabled={false}
        />
        {/* Bottom ellipse (renders on top of rect bottom edge) */}
        <Ellipse
          x={w / 2} y={h - eH / 2}
          radiusX={Math.max(1, w / 2)} radiusY={Math.max(1, eH / 2)}
          fill={fill} stroke={bodyStroke} strokeWidth={bodySw}
          perfectDrawEnabled={false}
        />
        {/* Top accent ellipse (renders on top of everything) */}
        <Ellipse
          x={w / 2} y={eH / 2}
          radiusX={Math.max(1, w / 2)} radiusY={Math.max(1, eH / 2)}
          fill={accentColor} stroke={bodyStroke} strokeWidth={bodySw}
          listening={false} perfectDrawEnabled={false}
        />
      </>
    );
  }

  if (type === 'queue') {
    // Ribbon / parallelogram — offset top-left and bottom-right
    const sk = Math.min(22, w * 0.14); // skew offset
    return (
      <>
        {/* Shadow */}
        <Line
          points={[sk + 2, 4, w + 2, 4, w - sk + 2, h + 4, 2, h + 4]}
          closed fill={palette.nodeShadow}
          listening={false} perfectDrawEnabled={false}
        />
        {/* Body */}
        <Line
          points={[sk, 0, w, 0, w - sk, h, 0, h]}
          closed fill={fill} stroke={stroke} strokeWidth={sw}
          perfectDrawEnabled={false}
        />
        {/* Tint */}
        <Line
          points={[sk, 0, w, 0, w - sk, h, 0, h]}
          closed fill={tint}
          listening={false} perfectDrawEnabled={false}
        />
        {/* Accent top strip */}
        <Line
          points={[sk, 0, w, 0, w - sk * 0.1, h * 0.2, sk * 0.9, h * 0.2]}
          closed fill={accentColor}
          listening={false} perfectDrawEnabled={false}
        />
      </>
    );
  }

  // ── General shapes — full accent-colored border, subtle tint fill ──────
  if (type === 'box') {
    const cr = 8;
    return (
      <>
        <Rect
          x={2} y={4} width={w} height={h}
          cornerRadius={cr} fill={palette.nodeShadow}
          listening={false} perfectDrawEnabled={false}
        />
        <Rect
          x={0} y={0} width={w} height={h}
          cornerRadius={cr} fill={fill} stroke={accentColor} strokeWidth={gSw}
          perfectDrawEnabled={false}
        />
        <Rect
          x={0} y={0} width={w} height={h}
          cornerRadius={cr} fill={tint}
          listening={false} perfectDrawEnabled={false}
        />
      </>
    );
  }

  if (type === 'oval') {
    const rx = Math.max(1, w / 2), ry = Math.max(1, h / 2);
    return (
      <>
        <Ellipse
          x={rx + 2} y={ry + 4} radiusX={rx} radiusY={ry}
          fill={palette.nodeShadow} strokeWidth={0}
          listening={false} perfectDrawEnabled={false}
        />
        <Ellipse
          x={rx} y={ry} radiusX={rx} radiusY={ry}
          fill={fill} stroke={accentColor} strokeWidth={gSw}
          perfectDrawEnabled={false}
        />
        <Ellipse
          x={rx} y={ry} radiusX={rx} radiusY={ry}
          fill={tint} strokeWidth={0}
          listening={false} perfectDrawEnabled={false}
        />
      </>
    );
  }

  if (type === 'rounded') {
    const cr = h * 0.42;
    return (
      <>
        <Rect
          x={2} y={4} width={w} height={h}
          cornerRadius={cr} fill={palette.nodeShadow}
          listening={false} perfectDrawEnabled={false}
        />
        <Rect
          x={0} y={0} width={w} height={h}
          cornerRadius={cr} fill={fill} stroke={accentColor} strokeWidth={gSw}
          perfectDrawEnabled={false}
        />
        <Rect
          x={0} y={0} width={w} height={h}
          cornerRadius={cr} fill={tint}
          listening={false} perfectDrawEnabled={false}
        />
      </>
    );
  }

  if (type === 'hexagon') {
    const rot = ((Math.round(shapeRotation) % 180) + 180) % 180;
    let pts: number[];
    if (rot === 90) {
      // Portrait / pointy-top hexagon
      const oy = h * 0.25;
      pts = [w / 2, 0, w, oy, w, h - oy, w / 2, h, 0, h - oy, 0, oy];
    } else {
      // Landscape / flat-top hexagon (default)
      const ox = w * 0.25;
      pts = [ox, 0, w - ox, 0, w, h / 2, w - ox, h, ox, h, 0, h / 2];
    }
    const shadowPts = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadowPts} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'triangle') {
    const rot = ((Math.round(shapeRotation) % 360) + 360) % 360;
    let pts: number[];
    let shadowPts: number[];
    if (rot === 90) {
      // Down-pointing
      pts = [0, 0, w, 0, w / 2, h];
      shadowPts = [2, 4, w + 2, 4, w / 2 + 2, h + 4];
    } else if (rot === 180) {
      // Left-pointing
      pts = [w, 0, w, h, 0, h / 2];
      shadowPts = [w + 2, 4, w + 2, h + 4, 2, h / 2 + 4];
    } else if (rot === 270) {
      // Up-pointing
      pts = [0, h, w, h, w / 2, 0];
      shadowPts = [2, h + 4, w + 2, h + 4, w / 2 + 2, 4];
    } else {
      // Right-pointing (default 0°)
      pts = [0, 0, 0, h, w, h / 2];
      shadowPts = [2, 4, 2, h + 4, w + 2, h / 2 + 4];
    }
    return (
      <>
        <Line points={shadowPts} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'cross') {
    const arm = w * 0.33;
    const cx = (w - arm) / 2;
    const cy = (h - arm) / 2;
    const pts = [
      cx, 0,  cx + arm, 0,  cx + arm, cy,
      w, cy,  w, cy + arm,  cx + arm, cy + arm,
      cx + arm, h,  cx, h,  cx, cy + arm,
      0, cy + arm,  0, cy,  cx, cy,
    ];
    const shadowPts = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadowPts} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'net_cloud') {
    const pts = cloudPoints(w, h);
    const shadow = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadow} closed fill={palette.nodeShadow} tension={0.35} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} tension={0.35} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} tension={0.35} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'net_subnet') {
    return kitRect(w, h, 10, fill, tint, accentColor, palette.nodeShadow, gSw, [8, 5]);
  }

  if (type === 'net_host') {
    const bodyH = h - 14;
    return (
      <>
        {kitRect(w, bodyH, 6, fill, tint, accentColor, palette.nodeShadow, gSw)}
        <Rect x={w / 2 - 8} y={bodyH} width={16} height={6} fill={accentColor} opacity={0.7} listening={false} perfectDrawEnabled={false} />
        <Rect x={w / 2 - 18} y={h - 6} width={36} height={6} cornerRadius={2} fill={accentColor} opacity={0.5} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'net_firewall') {
    return (
      <>
        {kitRect(w, h, 4, fill, tint, accentColor, palette.nodeShadow, gSw)}
        {[0, 1, 2].map(row => (
          <Rect
            key={row}
            x={8 + (row % 2) * 8} y={10 + row * ((h - 20) / 3)}
            width={w - 24} height={Math.max(4, (h - 28) / 5)}
            fill={accentColor} opacity={0.35}
            listening={false} perfectDrawEnabled={false}
          />
        ))}
      </>
    );
  }

  if (type === 'net_router') {
    const rx = Math.max(1, w / 2), ry = Math.max(1, h / 2);
    return (
      <>
        <Ellipse x={rx + 2} y={ry + 4} radiusX={rx} radiusY={ry} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
        <Ellipse x={rx} y={ry} radiusX={rx} radiusY={ry} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Ellipse x={rx} y={ry} radiusX={rx} radiusY={ry} fill={tint} listening={false} perfectDrawEnabled={false} />
        <Line points={[rx - rx * 0.45, ry, rx + rx * 0.45, ry]} stroke={accentColor} strokeWidth={1.5} listening={false} perfectDrawEnabled={false} />
        <Line points={[rx, ry - ry * 0.45, rx, ry + ry * 0.45]} stroke={accentColor} strokeWidth={1.5} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'net_lb') {
    const ox = w * 0.22;
    const pts = [ox, 0, w - ox, 0, w, h / 2, w - ox, h, ox, h, 0, h / 2];
    const shadow = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadow} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'net_switch') {
    return kitRect(w, h, h * 0.35, fill, tint, accentColor, palette.nodeShadow, gSw);
  }

  if (type === 'pipe_source' || type === 'pipe_sink') {
    const cut = Math.min(20, w * 0.16);
    const pts = type === 'pipe_source'
      ? [0, 0, w - cut, 0, w, h / 2, w - cut, h, 0, h]
      : [cut, 0, w, 0, w, h, cut, h, 0, h / 2];
    const shadow = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadow} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'pipe_process') {
    const cut = Math.min(22, w * 0.18);
    const pts = [0, 0, w - cut, 0, w, h / 2, w - cut, h, 0, h, cut, h / 2];
    const shadow = pts.map((v, i) => (i % 2 === 0 ? v + 2 : v + 4));
    return (
      <>
        <Line points={shadow} closed fill={palette.nodeShadow} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Line points={pts} closed fill={tint} strokeWidth={0} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'pipe_store') {
    const eH = 14;
    return (
      <>
        <Rect x={2} y={4 + eH / 2} width={w} height={h - eH} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
        <Rect x={0} y={eH / 2} width={w} height={h - eH} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Ellipse x={w / 2} y={eH / 2} radiusX={Math.max(1, w / 2)} radiusY={Math.max(1, eH / 2)} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Ellipse x={w / 2} y={h - eH / 2} radiusX={Math.max(1, w / 2)} radiusY={Math.max(1, eH / 2)} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Rect x={0} y={eH / 2} width={w} height={h - eH} fill={tint} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'pipe_queue') {
    return kitRect(w, h, 8, fill, tint, accentColor, palette.nodeShadow, gSw);
  }

  if (type === 'uml_class' || type === 'uml_interface' || type === 'uml_enum') {
    return kitRect(w, h, 4, fill, tint, accentColor, palette.nodeShadow, gSw);
  }

  if (type === 'uml_package') {
    const tabW = Math.min(72, w * 0.4);
    const tabH = 16;
    return (
      <>
        <Rect x={2} y={4} width={tabW} height={tabH} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
        <Rect x={2} y={4 + tabH} width={w} height={h - tabH} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
        <Rect x={0} y={0} width={tabW} height={tabH} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Rect x={0} y={tabH - 1} width={w} height={h - tabH + 1} fill={fill} stroke={accentColor} strokeWidth={gSw} perfectDrawEnabled={false} />
        <Rect x={0} y={tabH - 1} width={w} height={h - tabH + 1} fill={tint} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  if (type === 'uml_actor') {
    const cx = w / 2;
    const headR = Math.min(12, w * 0.22);
    const headY = 16;
    const bodyTop = headY + headR + 4;
    const bodyBot = h - 28;
    const hipY = bodyBot;
    return (
      <>
        <Ellipse x={cx + 1} y={headY + 2} radiusX={headR} radiusY={headR} fill={palette.nodeShadow} listening={false} perfectDrawEnabled={false} />
        <Ellipse x={cx} y={headY} radiusX={headR} radiusY={headR} stroke={accentColor} strokeWidth={gSw} fill={fill} perfectDrawEnabled={false} />
        <Line points={[cx, bodyTop, cx, hipY]} stroke={accentColor} strokeWidth={gSw} listening={false} perfectDrawEnabled={false} />
        <Line points={[cx - 18, bodyTop + 10, cx + 18, bodyTop + 10]} stroke={accentColor} strokeWidth={gSw} listening={false} perfectDrawEnabled={false} />
        <Line points={[cx, hipY, cx - 16, h - 10]} stroke={accentColor} strokeWidth={gSw} listening={false} perfectDrawEnabled={false} />
        <Line points={[cx, hipY, cx + 16, h - 10]} stroke={accentColor} strokeWidth={gSw} listening={false} perfectDrawEnabled={false} />
      </>
    );
  }

  return null;
};

function kitRect(
  w: number,
  h: number,
  radius: number,
  fill: string,
  tint: string,
  accent: string,
  shadow: string,
  sw: number,
  dash?: number[],
) {
  return (
    <>
      <Rect x={2} y={4} width={w} height={h} cornerRadius={radius} fill={shadow} listening={false} perfectDrawEnabled={false} />
      <Rect x={0} y={0} width={w} height={h} cornerRadius={radius} fill={fill} stroke={accent} strokeWidth={sw} dash={dash} perfectDrawEnabled={false} />
      <Rect x={0} y={0} width={w} height={h} cornerRadius={radius} fill={tint} listening={false} perfectDrawEnabled={false} />
    </>
  );
}

function cloudPoints(w: number, h: number): number[] {
  const y = h * 0.62;
  return [
    w * 0.08, y,
    w * 0.12, h * 0.38,
    w * 0.32, h * 0.22,
    w * 0.5, h * 0.16,
    w * 0.68, h * 0.22,
    w * 0.88, h * 0.38,
    w * 0.92, y,
    w * 0.78, h * 0.88,
    w * 0.22, h * 0.88,
  ];
}
